import produce from 'immer';
import React, {useEffect} from 'react';

import {FormDefinitionContent, FormDefinitionField, FormSectionType} from 'forms/form-designer/Constants';
import {getStructuredTableAdapters} from 'notebook/structured-tables/StructuredTableAdapters';
import StructuredTableHelper from 'notebook/structured-tables/StructuredTableHelper';
import {EvaluatedTable} from 'notebook/tables/excess/Evaluator';
import {
  evaluateTables,
  evaluateTablesWithResolutions,
} from 'notebook/tables/spreadsheets/SpreadsheetEvalUtil';
import {Table, TypedValue} from 'notebook/tables/TableConstants';
import {
  areTypedValuesEquivalent,
  formatTypedValue,
  isTypedValueEmpty,
} from 'notebook/tables/typed-values/DataTypes';
import useStateIfMounted from 'ui-library/hooks/useStateIfMounted';

import {
  FieldKeyToTableColumnIndex,
  FormInstanceComputedTableState,
  FormInstanceTableState,
  TableColumnIndex,
} from './Constants';

export function getTableCidToStateIdentifier(
  structuredTables: FormInstanceTableState
): Record<string, string> {
  return Object.entries(structuredTables).reduce<Record<string, string>>(
    (idMap, [tableKey, structuredTable]) => {
      idMap[structuredTable.cid] = tableKey;
      return idMap;
    },
    {}
  );
}

export function getFullTableMap(structuredTables: FormInstanceTableState): Record<string, Table> {
  return Object.entries(structuredTables).reduce<Record<string, Table>>(
    (tableMap, [tableKey, structuredTable]) => {
      tableMap[structuredTable.cid] =
        getStructuredTableAdapters()[structuredTable.kind].toTable(structuredTable);
      return tableMap;
    },
    {}
  );
}

export function getTablesByTableyKeyFromCid(
  computedTablesByCid: Record<string, EvaluatedTable>,
  tableCidToStateIdentifier: Record<string, string>
): FormInstanceComputedTableState {
  return Object.entries(computedTablesByCid).reduce<FormInstanceComputedTableState>(
    (computedTablesByTableKey, [tableCid, computedTable]) => {
      computedTablesByTableKey[tableCidToStateIdentifier[tableCid]] = computedTable;
      return computedTablesByTableKey;
    },
    {}
  );
}

export function buildComputedTables(
  structuredTables: FormInstanceTableState
): FormInstanceComputedTableState {
  const tableCidToStateIdentifier = getTableCidToStateIdentifier(structuredTables);
  const fullTableMap = getFullTableMap(structuredTables);
  const {computedTables} = evaluateTables({
    fullTableMap,
    oldComputedTables: null,
    hasFormulasDisabled: false,
    selectors: [],
    spreadsheetEvalCacheKey: null,
  });
  return getTablesByTableyKeyFromCid(computedTables, tableCidToStateIdentifier);
}

export async function buildAsyncComputedTables(
  structuredTables: FormInstanceTableState
): Promise<FormInstanceComputedTableState> {
  const tableCidToStateIdentifier = getTableCidToStateIdentifier(structuredTables);
  const fullTableMap = getFullTableMap(structuredTables);
  const {computedTables} = await evaluateTablesWithResolutions({
    fullTableMap,
    hasFormulasDisabled: false,
    selectors: [],
  });
  return getTablesByTableyKeyFromCid(computedTables, tableCidToStateIdentifier);
}

export function useAsyncComputedTables(
  formInstanceTableState: FormInstanceTableState
): FormInstanceComputedTableState {
  const [computedTables, setComputedTables] = useStateIfMounted<FormInstanceComputedTableState>(
    buildComputedTables(formInstanceTableState)
  );
  useEffect(() => {
    setComputedTables(buildComputedTables(formInstanceTableState));
    buildAsyncComputedTables(formInstanceTableState).then((newComputedTables) => {
      setComputedTables(newComputedTables);
    });
  }, [formInstanceTableState, setComputedTables]);

  return computedTables;
}

export interface AffectedTableColumnIndexes {
  hiddenColumnsToClear: Array<TableColumnIndex>;
  sharedColumnsToNormalize: Array<TableColumnIndex>;
}

/** Given a form definition and a table's current state, identify the columns that will either be cleared or
 * normalized to a single value because of the form's shared and hidden fields.
 */
export function determineAffectedTableColumnIndexes(
  tableState: FormInstanceTableState,
  computedTableState: FormInstanceComputedTableState,
  formDefinition: FormDefinitionContent,
  fieldKeyToTableColumnIndex: FieldKeyToTableColumnIndex
): AffectedTableColumnIndexes {
  // HIDDEN FIELDS
  const hiddenColumnIndexes = getHiddenTableColumnIndexes(tableState, fieldKeyToTableColumnIndex);
  // if one of the rows of the table has a filled in value, then note that that column will be cleared
  const hiddenColumnsToClear: Array<TableColumnIndex> = hiddenColumnIndexes.filter(
    ([tableKey, columnIndex]) =>
      computedTableState[tableKey].some((row) => !isTypedValueEmpty(row[columnIndex].value))
  );

  // SHARED FIELDS
  const [sharedFields] = partitionFormSharedRepeatableFields(formDefinition);
  const sharedColumnsIndexes: Array<TableColumnIndex> = sharedFields.map(
    (field) => fieldKeyToTableColumnIndex[field.key]
  );
  const sharedColumnsToNormalize: Array<TableColumnIndex> = sharedColumnsIndexes.filter(
    ([tableKey, columnIndex]) => !areAllCellsInColumnEquivalent(computedTableState[tableKey], columnIndex)
  );
  return {
    hiddenColumnsToClear,
    sharedColumnsToNormalize,
  };
}

/** Finds the table columns that are hidden in the form definition */
function getHiddenTableColumnIndexes(
  tableState: FormInstanceTableState,
  fieldKeyToTableColumnIndex: FieldKeyToTableColumnIndex
): Array<TableColumnIndex> {
  let hiddenTableColumnIndexes: Array<TableColumnIndex> = [];
  for (const [tableKey, table] of Object.entries(tableState)) {
    const allColumnIndexes = [...Array(table.columns.length).keys()];
    /** the columns that do have a field in the form */
    const shownColumnIndexes = new Set(
      Object.values(fieldKeyToTableColumnIndex)
        .filter(([tk, columnIndex]) => tk === tableKey)
        .map(([tk, columnIndex]) => columnIndex)
    );
    /** the columns that are missing from the form */
    hiddenTableColumnIndexes = hiddenTableColumnIndexes.concat(
      allColumnIndexes
        .filter((columnIndex) => !shownColumnIndexes.has(columnIndex))
        .map((columnIndex) => [tableKey, columnIndex])
    );
  }
  return hiddenTableColumnIndexes;
}

/** Split a form's fields into those that are shared vs repeatable */
export function partitionFormSharedRepeatableFields(
  formDefinitionContent: FormDefinitionContent
): [sharedFields: Array<FormDefinitionField>, repeatableFields: Array<FormDefinitionField>] {
  let sharedFields: Array<FormDefinitionField> = [];
  let repeatableFields: Array<FormDefinitionField> = [];
  for (const section of formDefinitionContent.sections) {
    if (section.sectionType === FormSectionType.ITERABLE) {
      repeatableFields = [
        ...repeatableFields,
        ...section.content.filter(
          (sectionOrField): sectionOrField is FormDefinitionField => sectionOrField.type === 'FormField'
        ),
      ];
    } else if (section.sectionType === FormSectionType.STATIC) {
      sharedFields = [
        ...sharedFields,
        ...section.content.filter(
          (sectionOrField): sectionOrField is FormDefinitionField => sectionOrField.type === 'FormField'
        ),
      ];
    }
  }
  return [sharedFields, repeatableFields];
}

/** Returns true when all typed values in an array are equal. Returns true when there are only 0 or 1 values in the array. */
function areAllTypedValuesEquivalent(typedValues: Array<TypedValue | null>): boolean {
  if (typedValues.length > 1) {
    for (let i = 1; i < typedValues.length; i++) {
      if (!areTypedValuesEquivalent(typedValues[i - 1], typedValues[i])) {
        return false;
      }
    }
  }
  return true;
}

/** Checks a table's column to see if all typed values are equal. */
function areAllCellsInColumnEquivalent(computedTable: EvaluatedTable, columnIndex: number): boolean {
  const allTypedValues = computedTable.map((row) => row[columnIndex].value);
  return areAllTypedValuesEquivalent(allTypedValues);
}

/** From a table's affected columns, generate a list of warnings that describe the changes */
export function makeAffectedTableColumnWarnings(
  tableState: FormInstanceTableState,
  computedTableState: FormInstanceComputedTableState,
  {sharedColumnsToNormalize, hiddenColumnsToClear}: AffectedTableColumnIndexes
): Array<JSX.Element> {
  const sharedColumnWarnings: Array<JSX.Element> = sharedColumnsToNormalize.map(([tableKey, columnIndex]) => {
    const sharedCell = tableState[tableKey].rows[0].cells[columnIndex];
    const sharedValue = computedTableState[tableKey][0][columnIndex].value;
    const column = tableState[tableKey].columns[columnIndex];
    return (
      <li>
        <b>{column.name}</b> will be replaced with{' '}
        {isTypedValueEmpty(sharedValue) ? <em>Blank</em> : `"${formatTypedValue(sharedValue, sharedCell)}"`}
      </li>
    );
  });

  const hiddenColumnWarnings: Array<JSX.Element> = hiddenColumnsToClear.map(([tableKey, columnIndex]) => {
    const column = tableState[tableKey].columns[columnIndex];
    return (
      <li>
        <b>{column.name}</b> will be replaced with <em>Blank</em>
      </li>
    );
  });

  return [...sharedColumnWarnings, ...hiddenColumnWarnings];
}

/** Use the affected table column indexes to fix the table state to have the correct values for hidden and shared fields */
export function applyAffectedTableColumnFixes(
  tableState: FormInstanceTableState,
  computedTableState: FormInstanceComputedTableState,
  {sharedColumnsToNormalize, hiddenColumnsToClear}: AffectedTableColumnIndexes
): FormInstanceTableState {
  return produce(tableState, (draft) => {
    // make all values in column the same as the first row
    for (const [tableKey, columnIndex] of sharedColumnsToNormalize) {
      const sharedCell = tableState[tableKey].rows[0].cells[columnIndex];
      StructuredTableHelper.forCellInColumn(draft[tableKey], columnIndex, (cell) => {
        cell.typedValue = sharedCell.typedValue;
        cell.text = sharedCell.text;
      });
    }
    // clear out all values of cells in columns
    for (const [tableKey, columnIndex] of hiddenColumnsToClear) {
      StructuredTableHelper.clearColumn(draft[tableKey], columnIndex);
    }
  });
}
